<template>
  <PlatformRenderer>
    <template v-slot:onMobile>
      <div class="w-100">
        <WCSpinner v-if="loading || isLoading" size="xl" position="window-center" />
        <div class="p-3 d-flex align-items-center justify-content-between wc-mobile-order__type">
          <h3 v-if="search.filter === 'IN_PROGRESS'" class="m-0">{{ $t('ordersInProgress') }}</h3>
          <h3 v-if="search.filter === 'COMPLETED'" class="m-0">{{ $t('yourCompletedOrders') }}</h3>
          <h3 v-if="search.filter === ''" class="m-0">{{ $t('allOrders') }}</h3>
          <WCSelect
            class="wc-filter"
            v-model="search.filter"
            @input="searchChanged()"
            :aria-label="$t('selectOrderType')"
          >
            <option value="">{{ $t('allOrders') }}</option>
            <option value="COMPLETED">{{ $t('completed') }}</option>
            <option value="IN_PROGRESS">{{ $t('inProgress') }}</option>
          </WCSelect>
        </div>
        <div class="w-100" v-if="!loading">
          <div v-for="order in orders" :key="order.id">
            <WCMobileOrderPanel
              :order="order"
              @receipt="viewReceipt(order)"
              @cancelOrder="cancelOrder"
              @editOrder="editOrder"
            />
          </div>
          <p v-if="!loadError && orders && !orders.length" class="text-center font-size-lg mt-3">
            {{ $t('noOrders') }}
          </p>
          <p v-if="loadError" class="text-center font-size-lg mt-3">{{ loadError }}</p>
          <template v-if="!loadError && orders && orders.length > 0">
            <WCPagination
              :pageLinks="false"
              :boundaryLinks="false"
              :collectionSize="count"
              v-model="search.pn"
              :pageSize="4"
              :maxSize="7"
              @input="updateSearchParams"
            />
            <p class="text-center">
              {{ $t('showingOrders') }} {{ startRecord }} {{ $t('to') }} {{ endRecord }}.
            </p>
          </template>
        </div>
      </div>
    </template>
    <template v-slot:onDesktop>
      <DashboardWindow>
        <template v-slot:title>
          {{ $t('orderHistory') }}
        </template>

        <template v-slot:title-right>
          <WCSelect
            class=""
            v-model="search.filter"
            @input="searchChanged()"
            :aria-label="$t('selectOrderType')"
          >
            <option value="">{{ $t('allOrders') }}</option>
            <option value="COMPLETED">{{ $t('completed') }}</option>
            <option value="IN_PROGRESS">{{ $t('inProgress') }}</option>
          </WCSelect>
        </template>

        <template v-slot:body>
          <WCSpinner v-if="loading || isLoading" size="xl" position="center" />

          <DashboardWindowSection v-else class="wc-order__type font-size-lg">
            <h3 v-if="search.filter === 'IN_PROGRESS'">{{ $t('ordersInProgress') }}</h3>
            <h3 v-if="search.filter === 'COMPLETED'">{{ $t('yourCompletedOrders') }}</h3>
            <h3 v-if="search.filter === ''">{{ $t('allOrders') }}</h3>
            <div class="w-100 pt-3" v-if="!loading">
              <WCOrderPanel
                class="mb-4"
                v-for="order in orders"
                :key="order.id"
                :order="order"
                @cancelOrder="cancelOrder"
                @editOrder="editOrder(order)"
                @receipt="viewReceipt(order)"
              />
              <p v-if="!loadError && orders && !orders.length" class="text-center">
                {{ $t('noOrders') }}
              </p>
              <p v-if="loadError" class="text-center">{{ loadError }}</p>
              <template v-if="!loadError && orders && orders.length > 0">
                <WCPagination
                  :pageLinks="false"
                  :boundaryLinks="false"
                  :collectionSize="count"
                  v-model="search.pn"
                  :pageSize="4"
                  :maxSize="7"
                  @input="updateSearchParams"
                />
                <p class="text-center">
                  {{ $t('showingOrders') }} {{ startRecord }} {{ $t('to') }} {{ endRecord }}.
                </p>
              </template>
            </div>
          </DashboardWindowSection>
        </template>
      </DashboardWindow>
    </template>
  </PlatformRenderer>
</template>

<script>
import PlatformRenderer from '@/modules/platform/components/PlatformRenderer';
import axios from 'axios';
import WCSelect from '@/modules/forms/components/WCSelect/WCSelect.vue';
import DashboardWindow from '@/layouts/components/Dashboard/DashboardWindow.vue';
import DashboardWindowSection from '@/layouts/components/Dashboard/DashboardWindowSection.vue';
import WCMobileOrderPanel from '@/modules/orders/components/WCMobileOrderPanel/WCMobileOrderPanel.vue';
import { mapActions, mapGetters } from 'vuex';
import WCOrderPanel from '../../components/WCOrderPanel/WCOrderPanel.vue';
import WCSpinner from '../../../../components/WCSpinner/WCSpinner.vue';
import WCPagination from '../../../../components/WCPagination/WCPagination.vue';
import OrderPanelMixin from '../../mixins/OrderPanelMixin';

export default {
  components: {
    PlatformRenderer,
    DashboardWindowSection,
    DashboardWindow,
    WCOrderPanel,
    WCMobileOrderPanel,
    WCSelect,
    WCSpinner,
    WCPagination,
  },
  created() {
    this.routeUpdate();
  },
  data() {
    return {
      search: null,
      orders: {},
      count: -1,
      loadError: null,
      loading: false,
    };
  },

  mixins: [OrderPanelMixin],

  methods: {
    ...mapActions({
      reloadCart: 'cart/reload',
    }),
    routeUpdate() {
      this.search = this.parseQueryString();
      this.updateData();
    },
    async updateData() {
      this.loading = true;
      this.orders = {};

      const { pn, ps } = this.search;
      let { filter } = this.search;
      filter = filter === '' ? null : filter;
      try {
        const response = await axios.post('/api/me/orders', { pn, ps, filter });
        this.orders = response.data.orders;
        this.count = response.data.count;

        this.loadError = null;
      } catch (error) {
        const code = error.payload && error.payload.code ? error.payload.code : 500;
        this.loadError = `Order History is not currently available. Please try again later. Code ${code}.`;
      } finally {
        this.loading = false;
      }
    },
    parseQueryString() {
      const parsed = {};
      const params = this.$route.query;

      if (params.pn) {
        parsed.pn = parseInt(params.pn, 10);
        if (parsed.pn <= 0) {
          parsed.pn = 0;
        }
      } else {
        parsed.pn = 1;
      }

      parsed.filter = params.filter ? params.filter : '';

      parsed.ps = 4;

      return parsed;
    },
    updateSearchParams() {
      this.$router.push({
        path: '/me/orders',
        query: {
          ...this.search,
          pn: this.search.pn === 1 ? undefined : this.search.pn,
          ps: this.search.ps === 4 ? undefined : this.search.ps,
          filter: this.search.filter === '' ? undefined : this.search.filter,
        },
      });
    },
    searchChanged() {
      // We don't want people filtering and then seeing no results
      this.search.pn = 1;

      // update the url
      this.updateSearchParams();
    },
    previousPage() {
      this.search.pn -= 1;
      this.updateSearchParams();
    },
    nextPage() {
      this.search.pn += 1;
      this.updateSearchParams();
    },
  },
  computed: {
    ...mapGetters({
      isLoading: 'cart/isLoading',
      lineItems: 'cart/getLineItems',
    }),
    hasPrevious() {
      return this.search.pn > 1;
    },
    hasNext() {
      return this.orders.length <= this.search.ps && !this.orders.length <= 0;
    },
    startRecord() {
      return this.orders.length > 0 ? (this.search.pn - 1) * this.search.ps + 1 : 0;
    },
    endRecord() {
      return Math.min(this.count, this.search.pn * this.search.ps);
    },
  },
  watch: {
    $route: 'routeUpdate', // Anytime $router is used, the routeUpdate method is called
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-mobile-order__type {
  background-color: var(--secondary-lighter-7, get-color('secondary', 'lighter-7'));
}

.wc-filter {
  width: 40%;
}
</style>
